// src/themes/palette.ts

// material-ui
import { alpha, createTheme } from '@mui/material/styles';

// third-party
import { presetDarkPalettes, presetPalettes, PalettesProps } from '@ant-design/colors';

// project imports
import ThemeOption from './theme';
import { ThemeMode } from 'config';

// types
import { PaletteThemeProps } from 'types/theme';
import { PresetColor } from 'types/config';

// ==============================|| DEFAULT THEME - PALETTE ||============================== //

export default function Palette(mode: ThemeMode, presetColor: PresetColor) {
  const baseColors: PalettesProps =
    mode === ThemeMode.DARK ? presetDarkPalettes : presetPalettes;

  // --- Weepee custom 10-step ramp (used for primary via blue[]) ---
  const myBlue: string[] = [
    '#fffce6', '#fff3a3', '#ffe97a', '#ffdc52', '#ffcd29',
    '#ffbb00', '#d99800', '#b37700', '#8c5900', '#663d00'
  ];

  // Start from AntD palettes, but replace blue with your ramp
  const themedColors: PalettesProps = {
    ...baseColors,
    blue: myBlue as PalettesProps['blue']
  };

  // Grey extension Mantis expects
  let greyPrimary = [
    '#ffffff', '#fafafa', '#f5f5f5', '#f0f0f0', '#d9d9d9',
    '#bfbfbf', '#8c8c8c', '#595959', '#262626', '#141414', '#000000'
  ];
  let greyAscent = ['#fafafa', '#bfbfbf', '#434343', '#1f1f1f'];
  let greyConstant = ['#fafafb', '#e6ebf1'];

  if (mode === ThemeMode.DARK) {
    greyPrimary = ['#000000', '#141414', '#1e1e1e', '#595959', '#8c8c8c', '#bfbfbf', '#d9d9d9', '#f0f0f0', '#f5f5f5', '#fafafa', '#ffffff'];
    greyAscent = ['#fafafa', '#bfbfbf', '#434343', '#1f1f1f'];
    greyConstant = ['#121212', '#d3d8db'];
  }
  themedColors.grey = [...greyPrimary, ...greyAscent, ...greyConstant];

  // Build palette mapping using your colors
  const paletteColor: PaletteThemeProps = ThemeOption(themedColors, presetColor, mode);

  // Base theme
  const base = createTheme({
    palette: {
      mode,
      common: { black: '#000', white: '#fff' },
      ...paletteColor,
      text: {
        primary: mode === ThemeMode.DARK
          ? alpha(paletteColor.grey[900]!, 0.87)
          : paletteColor.grey[700],
        secondary: mode === ThemeMode.DARK
          ? alpha(paletteColor.grey[900]!, 0.45)
          : paletteColor.grey[500],
        disabled: mode === ThemeMode.DARK
          ? alpha(paletteColor.grey[900]!, 0.1)
          : paletteColor.grey[400]
      },
      action: { disabled: paletteColor.grey[300] },
      divider: mode === ThemeMode.DARK
        ? alpha(paletteColor.grey[900]!, 0.05)
        : paletteColor.grey[200],
      background: {
        paper: mode === ThemeMode.DARK ? paletteColor.grey[100] : paletteColor.grey[0],
        default: paletteColor.grey.A50
      }
    }
  });

  // Force primary trio from your ramp to avoid any later overrides
  const theme = createTheme(base, {
    palette: {
      primary: {
        ...base.palette.primary,
        main: myBlue[5],  // #ffbb00
        light: myBlue[3], // #ffdc52
        dark:  myBlue[6], // #d99800
        // Bright scheme -> better contrast with dark text
        contrastText: '#000'
      }
    }
  });

  // Uncomment to verify in console:
  // console.log('PRIMARY MAIN:', theme.palette.primary.main);

  return theme;
}
